import axios from 'axios';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import React, { useEffect, useState, useLayoutEffect } from 'react';
import { Carousel } from 'react-responsive-carousel';
import Cookies from 'js-cookie'; 
import '../css/card.css';
import masterBookData from '../data/books-data.json'; 
import { URLS, SHOPIFY_STORES } from '../config'; 
import { hooks } from '../hooks';
import { svg } from '../assets/svg';
import { theme } from '../constants';
import { product } from '../product';
import { actions } from '../store/actions';
import { components } from '../components';
import { custom } from '../custom';
import { items } from '../items';
import { Link } from "react-router-dom";
const allProductsMap = new Map<string, string>();

// UPDATED extractInternalId to handle new JSON paths like bookImages17.json
const extractInternalId = (path: string) => {
    const match = path.match(/bookImages(\d+)\.json$/);
    return match ? match[1] : undefined;
};

const populateProductMap = () => {
    masterBookData.forEach((book: any) => {
        // Map main product
        if (book.book_images_json_path) {
            const internalId = extractInternalId(book.book_images_json_path);
            // Ensure the main product has a Shopify ID mapped
            if (internalId) {
                // Prefer US id first for the prepopulated map (map used as fallback)
                const shopifyId = book.shopify_product_id_US || book.shopify_product_id_IN || book.shopify_product_id;
                if (shopifyId) allProductsMap.set(internalId, shopifyId);
            }
        }
        // Map issues in bundles (assuming issue ID is used to check purchase)
        if (Array.isArray(book.issues)) {
            book.issues.forEach((issue: any) => {
                if (issue.book_images_json_path) {
                    const internalId = extractInternalId(issue.book_images_json_path);
                    // Use dynamic key (IN/US) for issues, or fall back to main shopify_product_id
                    const shopifyId = issue.shopify_product_id_US || issue.shopify_product_id_IN || issue.shopify_product_id;
                    if (internalId && shopifyId) {
                        allProductsMap.set(internalId, shopifyId);
                    }
                }
            });
        }
    });
};

populateProductMap();

export const Product: React.FC = () => {
    const navigate = useNavigate();
    const dispatch = hooks.useAppDispatch();
    const location = useLocation();
    const { pdid } = useParams<{ pdid: string }>(); 

    // Retrieve item details from navigation state or local storage
    const item = location.state?.item || JSON.parse(localStorage.getItem("selectedBookItem") || "null");

    const [loading, setLoading] = useState<boolean>(true); // Controls the main purchase check
    const [reviewsData, setReviewsData] = useState<any>([]);
    const [isLoading, setIsLoading] = useState<boolean>(false); // Controls general data loading (products, books, issues)
    const [issues, setIssues] = useState<any[]>([]);
    const [productsData, setProductsData] = useState<any>([]);
    const [booksData, setBooksData] = useState<any>([]);
    const [products, setProducts] = useState([]); 
    
    const [purchasedProductIds, setPurchasedProductIds] = useState<Set<string>>(new Set());
    const [isPurchased, setIsPurchased] = useState<boolean>(false);
    
    const [showFullDescription, setShowFullDescription] = useState(false);
    const [selectedSize, setSelectedSize] = useState<string>(item?.sizes?.[1] || '');
    const [selectedColor, setSelectedColor] = useState<string>(item?.colors?.[1]?.name || '');
    const [activeIndex, setActiveIndex] = useState(0);
    const [showWebView, setShowWebView] = useState(false);

    // Get the primary ID for logic
    const primaryInternalId = item?.id?.toString() || pdid; 
    // NOTE: pick store-specific ID when needed (computed later where storeKey is known)

    const modifiedItem = item ? {
        ...item,
        color: selectedColor,
        size: selectedSize,
    } : {};

    const getShopifyIdForCheck = (internalId: string | undefined): string | undefined => {
        if (!internalId) return undefined;
        const storeKey = localStorage.getItem('loggedInStoreKey') || Cookies.get('loggedInStoreKey') || 'USA';

        // Search through masterBookData including nested issues to find a matching path
        for (const b of masterBookData) {
            // Check main book path
            if (b.book_images_json_path) {
                const id = extractInternalId(b.book_images_json_path);
                if (id === internalId.toString()) {
                    return storeKey === 'INDIA' ? b.shopify_product_id_IN : b.shopify_product_id_US;
                }
            }

            // Check nested issues
            if (Array.isArray(b.issues)) {
                for (const issue of b.issues) {
                    if (issue.book_images_json_path) {
                        const iid = extractInternalId(issue.book_images_json_path);
                        if (iid === internalId.toString()) {
                            return storeKey === 'INDIA' ? issue.shopify_product_id_IN : issue.shopify_product_id_US;
                        }
                    }
                }
            }
        }

        // Fallback to prepopulated map (which now prefers US ids)
        return allProductsMap.get(internalId.toString());
    };

    useLayoutEffect(() => {
        window.scrollTo(0, 0);
    }, [location.pathname]);

    useEffect(() => {
        // Fetch general product/issue data and run the purchase check
        getData();
        fetchPurchasedProductIds();
    }, []);

    useEffect(() => {
        // This useEffect seems to fetch product data that is not part of getData, maybe keep separate
        fetch("/apis/products.json")
            .then(res => res.json())
            .then(data => setProducts(data))
            .catch(err => console.error(err));
    }, []);
    
    useEffect(() => {
        // Re-calculate purchase status whenever the purchased IDs or current item changes
        const finalId = getShopifyIdForCheck(primaryInternalId);

        console.log('[Product Debug] Checking purchase status for internalId', { primaryInternalId, finalId });
        console.log('[Product Debug] Current purchasedProductIds:', Array.from(purchasedProductIds));

        if (finalId) {
            // Check the set of owned numeric IDs against the current item's numeric Shopify ID
            const owned = purchasedProductIds.has(finalId);
            console.log('[Product Debug] isPurchased result:', owned, { finalId });
            setIsPurchased(owned);
        } else if (!loading) {
            // If we can't map the product ID and loading is finished, assume not purchased.
            console.log('[Product Debug] No finalId found; marking not purchased');
            setIsPurchased(false);
        }
    }, [primaryInternalId, purchasedProductIds, loading]);


    const handleSlideChange = (index: number) => {
        setActiveIndex(index);
    };

    const getData = async () => {
        setIsLoading(true); // Set general loading state
        try {
            // Assuming these URLs return the list of products/issues
            const issues = await axios.get(URLS.GET_DATA).then(res => res.data.issues);
            const products = await axios.get(URLS.GET_PRODUCTS).then(res => res.data.products);
            const books = await axios.get(URLS.GET_BOOKS).then(res => res.data.books);

            setBooksData(books);
            setProductsData(products);
            setIssues(issues); // Ensure issues state is updated
        } catch (error) {
            console.error(error);
        } finally {
            setIsLoading(false); // Clear general loading state
        }
    };

    const fetchPurchasedProductIds = async () => {
        // Find token
        const tokenFromLocal = typeof window !== 'undefined' ? localStorage.getItem('customerAccessToken') : null;
        const tokenFromCookie = Cookies.get('customerAccessToken');
        const token = tokenFromLocal || tokenFromCookie;
        if (!token) {
            setLoading(false); // Stop loading if no user is logged in
            return;
        }

        // Get store configuration
        const storeKey = localStorage.getItem('loggedInStoreKey') || Cookies.get('loggedInStoreKey') || 'USA';
        const storeConfig = SHOPIFY_STORES[storeKey as keyof typeof SHOPIFY_STORES] || SHOPIFY_STORES.USA;
        // This key is used for mapping the numeric Shopify ID back to our local product data structure
        const productKeyForMatching = storeKey === 'INDIA' ? 'shopify_product_id_IN' : 'shopify_product_id_US';

        console.log(`[Product Debug] Store Key used for purchase check: ${storeKey}`);
        console.log(`[Product Debug] API URL used: ${storeConfig.url}`);
        console.log(`[Product Debug] Product Key for JSON matching: ${productKeyForMatching}`);

        // GraphQL Query to fetch product IDs from the customer's last 10 orders
        const query = `
            query getCustomerOrders($customerAccessToken: String!) {
                customer(customerAccessToken: $customerAccessToken) {
                    orders(first: 10, reverse: true) {
                        edges {
                            node {
                                lineItems(first: 10) {
                                    edges {
                                        node {
                                            variant {
                                                product {
                                                    id
                                                }
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
            }
        `;

        try {
            console.log('[Product Debug] Beginning fetchPurchasedProductIds', { storeKey, tokenPresent: !!token });
          const fullUrl = storeConfig.url;
           const response = await fetch(fullUrl, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    // Note: Ensure storeConfig.token is the Storefront Access Token
                    'X-Shopify-Storefront-Access-Token': storeConfig.token, 
                },
                body: JSON.stringify({ query, variables: { customerAccessToken: token } }),
            });
            
            // This is for checking HTTP status (e.g., 401, 404, 500)
            if (!response.ok) {
                console.error(`[Product Debug] Shopify API HTTP Error Status: ${response.status} for store: ${storeKey}`);
                // Attempt to log error response body for more detail
                const errorBody = await response.text(); 
                console.error('[Product Debug] Error Response Text:', errorBody);
                setLoading(false);
                return;
            }

            const result = await response.json();
            // Log a trimmed version of the response for debugging
            try { console.log('[Product Debug] GraphQL result snippet:', JSON.stringify(result).slice(0,2000)); } catch(e) {}

            if (result.errors) {
                console.error(`[Product Debug] GraphQL Errors for ${storeKey}:`, result.errors);
            }
            
            const orders = result?.data?.customer?.orders?.edges || [];
            const ownedIds = new Set<string>(); // Stores numeric Shopify IDs (e.g., "7890123")

            orders.forEach((order: any) => {
                order.node.lineItems.edges.forEach((line: any) => {
                    const p = line.node.variant?.product;
                    if (!p || !p.id) return;

                    // Shopify ID format: "gid://shopify/Product/7890123"
                    const shopifyGlobalId = p.id;
                    const shopifyNumericId = shopifyGlobalId.split('/').pop();

                    if (!shopifyNumericId) return;

                    console.log('[Product Debug] Found purchased product global id / numeric id:', { shopifyGlobalId, shopifyNumericId });

                    // 1. Add the main product ID from the order line item
                    ownedIds.add(shopifyNumericId);

                    // 2. Check if this purchased product is a bundle. If so, add all contained issues' IDs to the set.
                    const matchingBook = masterBookData.find(
                        (book: any) => (book[productKeyForMatching] && book[productKeyForMatching] === shopifyNumericId) || (book.shopify_product_id && book.shopify_product_id === shopifyNumericId)
                    );
                    
                    if (matchingBook && Array.isArray(matchingBook.issues)) {
                        matchingBook.issues.forEach((issue: any) => {
                            const issueIdKey = storeKey === 'INDIA' ? 'shopify_product_id_IN' : 'shopify_product_id_US';
                            if (issue[issueIdKey]) {
                                ownedIds.add(issue[issueIdKey]);
                            }
                        });
                    }
                });
            });
            console.log('[Product Debug] Total purchased IDs found:', Array.from(ownedIds));
            setPurchasedProductIds(ownedIds);
        } catch (err) {
            // **CATCHES the 'TypeError: Failed to fetch' (CORS/Network error)**
            console.error('Error fetching purchased products:'); 
            console.error(err); // Prints the full error object, including TypeError details
        } finally {
            setLoading(false);
        }
    };

    const openPaymentPopup = () => {
        // This function is currently unused but kept for potential future use
        const paymentUrl = "https://festive.zinioapps.com/checkout/payment";
        const paymentWindow = window.open(paymentUrl, "Payment", "width=600,height=700,left=200,top=100");
        if (!paymentWindow) {
            alert("Popup blocked! Please allow popups for this site.");
        }
    };

    // --- Render Functions (Unchanged for brevity, except where noted) ---
    
    const renderHeader = (): JSX.Element => <components.Header goBack={true} line={true} basket={true} />;

    const renderCarousel = (): JSX.Element | null => {
        if (!item?.images || item.images.length === 0) return null;
        return (
            <div style={{ marginBottom: 22 }}>
                <Carousel
                    infiniteLoop={false}
                    showStatus={false}
                    showThumbs={false}
                    thumbWidth={22}
                    showIndicators={false}
                    showArrows={false}
                    onChange={handleSlideChange}
                    swipeable
                    emulateTouch
                >
                    {item.images.map((image: any, index: any) => (
                        <img
                            key={index}
                            src={image}
                            alt='Carousel'
                            style={{
                                width: '100%',
                                height: 350,
                                objectFit: 'contain',
                                backgroundColor: theme.colors.imageBackground,
                            }}
                        />
                    ))}
                </Carousel>
            </div>
        );
    };

    const renderIndicator = (): JSX.Element | null => {
        if (!item?.images || item.images.length === 0) return null;
        return (
            <div style={{ display: 'flex', justifyContent: 'center', paddingBottom: 20, borderBottom: `1px solid ${theme.colors.aliceBlue2}`, marginBottom: 20 }}>
                {item.images.map((_: any, index: number) => {
                    const isSelected = index === activeIndex;
                    return <div key={index} style={{ background: isSelected ? theme.colors.mainColor : '#E8EFF4', width: 22, height: 6, margin: '0 5px', borderRadius: 6 }} />;
                })}
            </div>
        );
    };

    const renderNameWithButton = (): JSX.Element | null => {
        if (!item?.name) return null;
        return (
            <div style={{ display: 'flex', padding: '0 20px', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
                <h3 style={{ ...theme.fonts.Mulish_700Bold, fontSize: 20, color: theme.colors.mainColor }}>{item.name}{pdid}</h3>
                <product.ProductInWishlist item={item} version={2} />
            </div>
        );
    };

    const renderDescription = (): JSX.Element | null => {
        if (!item?.description) return null;

        const requiresTruncation = item.description.length > 150; 

        return (
            <div className="product-description-container">
                <h5 className="product-description-title">
                    Description
                </h5>
                <p
                    className={`product-description-text ${!showFullDescription && requiresTruncation ? 'truncated' : ''}`}
                >
                    {item.description}
                </p>

                {requiresTruncation && (
                    <button
                        className="read-more-button"
                        onClick={() => setShowFullDescription(!showFullDescription)}
                    >
                        {showFullDescription ? "Show Less" : <svg.ReadMoreSvg />}
                    </button>
                )}
            </div>
        );
    };

    const renderBestSellers = (): JSX.Element => {
        return (
            <div style={{ marginBottom: 40, display: 'flex', flexDirection: 'column' }}>
                <components.BlockHeading
                    title='Recent Issues'
                    containerStyle={{
                        padding: '0 20px 0',
                        marginBottom: 14,
                    }}
                    viewAllOnClick={() => {
                        dispatch(actions.resetFilters());
                        navigate('/Shop', {
                            state: { products: productsData, title: 'Magazine' },
                        });
                    }}
                />
                <custom.ScrollView style={{ paddingLeft: 20, paddingRight: 20 }}>
                    {productsData?.map((dataItem: any, index: number, arra: any) => {
                        const isLast = index === arra.length - 1;
                        return (
                            <items.ProductCard
                                key={dataItem.id}
                                isLast={isLast}
                                item={dataItem}
                                version={1}
                            />
                        );
                    })}
                </custom.ScrollView>
            </div>
        );
    };

    const renderBooksSellers = (): JSX.Element => {
        return (
            <div style={{ marginBottom: 40, display: 'flex', flexDirection: 'column' }}>
                <components.BlockHeading
                    title='Special Issues'
                    containerStyle={{
                        padding: '0 20px 0',
                        marginBottom: 14,
                    }}
                    viewAllOnClick={() => {
                        dispatch(actions.resetFilters());
                        navigate('/Shop', {
                            state: { products: booksData, title: 'Magazine' },
                        });
                    }}
                />
                <custom.ScrollView style={{ paddingLeft: 20, paddingRight: 20 }}>
                    {booksData?.map((dataItem: any, index: number, arra: any) => {
                        const isLast = index === arra.length - 1;
                        return (
                            <items.ProductCard
                                key={dataItem.productId || index} 
                                isLast={isLast}
                                item={dataItem}
                                version={1}
                            />
                        );
                    })}
                </custom.ScrollView>
            </div>
        );
    };

    const renderInThisIssue = (): JSX.Element => {
        if (issues.length === 0) return <></>;
        return (
            <div style={{ marginBottom: 40 }}>
                <components.BlockHeading
                    title='In This Issue'
                    containerStyle={{
                        padding: '0 20px 0',
                        marginBottom: 14,
                    }}
                />
                <div
                    style={{
                        display: "flex",
                        overflowX: "auto",
                        whiteSpace: "nowrap",
                        padding: "10px 20px",
                        gap: 15,
                        scrollbarWidth: "none",
                        msOverflowStyle: "none",
                    }}
                >
                    {issues.map((issue) => (
                        <Link
                            key={issue.id}
                            to={`/article/${issue.id}`}
                            style={{ textDecoration: "none", color: "inherit" }}
                        >
                            <div
                                style={{
                                    minWidth: 380,
                                    maxWidth: 400,
                                    flexShrink: 0,
                                    background: "#fff",
                                    padding: 12,
                                    borderRadius: 10,
                                    boxShadow: "0 2px 5px rgba(0,0,0,0.2)",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                }}
                            >
                                <div style={{ flex: 1, paddingRight: 10 }}>
                                    <div
                                        className="issue-title"
                                        style={{
                                            fontWeight: "bold",
                                            fontSize: 16,
                                            marginBottom: 5,
                                            whiteSpace: "normal",
                                        }}
                                    >
                                        {issue.title}
                                    </div>
                                    <div
                                        className="issue-description"
                                        style={{
                                            fontSize: 14,
                                            color: "#555",
                                            marginBottom: 5,
                                            whiteSpace: "normal",
                                        }}
                                    >
                                        {issue.description}
                                    </div>
                                    <div
                                        className="issue-time"
                                        style={{
                                            fontSize: 12,
                                            color: "#888",
                                        }}
                                    >
                                        ⏱ {issue.readTime} min
                                    </div>
                                </div>
                                <img
                                    src={issue.image}
                                    alt={issue.title}
                                    style={{
                                        width: 65,
                                        height: 65,
                                        objectFit: "cover",
                                    }}
                                />
                            </div>
                        </Link>
                    ))}
                </div>
            </div>
        );
    };

    // UPDATED RENDER BUTTONS LOGIC
    const renderButtons = (): JSX.Element => {
        if (loading) 
            return <div style={{ height: 50, padding: '0 20px' }}><components.Loader /></div>; 

        const productIdForNavigation = item?.id || pdid; 
        const storeKey = localStorage.getItem('loggedInStoreKey') || Cookies.get('loggedInStoreKey') || 'USA';
        const shopifyProductToBuy = storeKey === 'INDIA' ? item?.shopify_product_id_IN : item?.shopify_product_id_US;
        const storeConfig = SHOPIFY_STORES[storeKey as keyof typeof SHOPIFY_STORES] || SHOPIFY_STORES.USA;
        
        // Construct the base URL for the store, assuming it's available in SHOPIFY_STORES
        const baseUrl = storeConfig.url.replace('/api/2025-07/graphql.json', '');

        if (isPurchased) {
            return (
                <components.Button
                    title='Read Now'
                    onClick={() => {
                        localStorage.setItem('selectedBook', JSON.stringify(item));
                        // Navigate to the book viewer using the internal ID
                        navigate(`/BookViewer/bookImages${productIdForNavigation}`, { state: { item } });
                    }}
                    containerStyle={{ marginBottom: 10, padding: '0 20px' }}
                />
            );
        } else {
            // Logic for "Buy Now" button
            let buyNowLink = 'https://festivehq.com/'; // Fallback link

            if (shopifyProductToBuy) {
                // Construct a direct product link or checkout link using the store's base URL
                // This assumes the product ID can be used in a standard /products/ URL structure.
                // NOTE: For true checkout, you need the Variant ID, not just the Product ID.
                buyNowLink = `${baseUrl}/products/${shopifyProductToBuy}`; 
            }

            return (
                <components.Button
                    title='Buy Now'
                    onClick={() => {
                        // Open the constructed link (or fallback)
                        window.location.href = buyNowLink;
                    }}
                    containerStyle={{ marginBottom: 10, padding: '0 20px' }}
                />
            );
        }
    };



    const renderReviews = (): JSX.Element => {
        const reviewQty = 3;
        if (reviewsData.length === 0) return <></>;
        return (
            <div>
                 <components.BlockHeading
                    title={`Reviews (${reviewsData.slice(0, reviewQty).length})`}
                    containerStyle={{
                        padding: '0 20px 0 20px',
                        marginBottom: 20,
                    }}
                    viewAllOnClick={() => {
                        navigate('/reviews', { state: { reviews: reviewsData } });
                    }}
                />
                {reviewsData
                    .slice(0, reviewQty)
                    .map((dataItem: any, index: number, array: any) => {
                        const isLast = index === array.length - 1;
                        return (
                            <items.ReviewItem
                                item={dataItem}
                                isLast={isLast}
                                key={dataItem.id.toString()}
                            />
                        );
                    })}
            </div>
        );
    };

    const renderContent = (): JSX.Element => {
        if (!item) return <div style={{ padding: 20, textAlign: 'center' }}><h2>Product Not Available</h2><p>We're sorry, the product you're looking for could not be displayed.</p><button onClick={() => navigate('/shop')}>Back to Shop</button></div>;
        if (isLoading) return <components.Loader />;

        return (
            <div style={{ padding: '0 0 20px 0' }}>
                 {renderCarousel()}
                {renderIndicator()}
                {renderNameWithButton()}
                {renderDescription()}
                {renderButtons()} 
             
                {renderBooksSellers()}
                {renderBestSellers()}
            </div>
        );
    };

    return (
        <>
            {renderHeader()}
            {renderContent()}
            <components.BottomTabBar />
        </>
    );
};